import { CLASSIC_SYMBOLS } from '../shared/classic-symbols';
import { SYMBOLS } from '../shared/symbols';
import { DEFAULT_CARD_BACK, collectionSymbolCount, type CardCollection, type EditableSymbol } from '../shared/collections';
import { symbolNamesPtBR } from './locales/common.pt-BR';
import type { Locale } from './i18n';
import { cropImage, prepareImage, DEFAULT_CROP } from './image-editor';
import { t } from './i18n';

export const CLASSIC_GAME_ID = 'builtin_classic_v2';
export const LEGACY_CLASSIC_GAME_ID = 'builtin_classic_v1';
export const classicAssetKey = (slug: string) => `classic2:${slug}`;
export interface CatalogAsset {
  key: string;
  name: string;
  image: string;
  thumbnail: string;
  /** High-quality WebP for building a portable local collection. */
  download?: string;
  origins: { id: string; name: string }[];
  editable?: EditableSymbol;
}

/** All editions and local games contribute, including unfinished drafts.
 * Exact raster duplicates share one tile but retain every source for filtering. */
export function catalogAssets(collections: readonly CardCollection[], locale: Locale): CatalogAsset[] {
  const classicName = locale === 'pt-BR' ? 'Clássico' : 'Classique';
  const assets: CatalogAsset[] = [
    ...CLASSIC_SYMBOLS.map(symbol => ({ key: classicAssetKey(symbol.slug), name: locale === 'pt-BR' ? symbol.pt : symbol.name,
      image: symbol.image, thumbnail: symbol.thumbnail, download: symbol.portable, origins: [{ id: 'classic', name: classicName }] })),
    ...SYMBOLS.map(symbol => ({ key: `nautico:${symbol.id}`, name: locale === 'pt-BR' ? symbolNamesPtBR[symbol.id] : symbol.name,
      image: symbol.assetPath!, thumbnail: symbol.assetPath!.replace('/symbols/', '/symbols/web/').replace(/\.png$/, '.webp'), origins: [{ id: 'nautico', name: 'Náutico / Recife' }] })),
    ...collections.flatMap(game => game.symbols.map(symbol => ({ key: `${game.id}:${symbol.id}`, name: symbol.name,
      image: symbol.image, thumbnail: symbol.image, origins: [{ id: game.id === CLASSIC_GAME_ID ? 'classic' : game.id, name: game.name }], editable: symbol }))),
  ];
  const imageKeys = new Map(collections.flatMap(game => game.symbols.filter(symbol => symbol.assetKey).map(symbol => [symbol.image, symbol.assetKey!] as const)));
  const unique = new Map<string, CatalogAsset>();
  for (const asset of assets) {
    const identity = asset.editable?.assetKey ?? imageKeys.get(asset.image) ?? (asset.editable ? asset.image : asset.key);
    const previous = unique.get(identity);
    if (previous) {
      for (const origin of asset.origins) if (!previous.origins.some(item => item.id === origin.id)) previous.origins.push(origin);
    } else unique.set(identity, asset);
  }
  return [...unique.values()];
}

const preparedAssets = new Map<string, Promise<EditableSymbol>>();
/** Trusted built-in files become portable raster data; personal crops remain intact. */
export async function materializeAsset(asset: CatalogAsset): Promise<EditableSymbol> {
  if (asset.editable) return structuredClone(asset.editable);
  let pending = preparedAssets.get(asset.image);
  if (!pending) {
    pending = (async () => {
      const response = await fetch(asset.download ?? asset.image, { signal: AbortSignal.timeout(20_000) });
      if (!response.ok) throw new Error(t('Impossible de charger cette illustration. Réessaie.'));
      const blob = await response.blob();
      const source = await prepareImage(new File([blob], 'illustration', { type: blob.type === 'image/webp' ? 'image/webp' : 'image/png' }));
      return { id: 0, assetKey: asset.key, name: asset.name, image: await cropImage(source, DEFAULT_CROP), source, crop: { ...DEFAULT_CROP } };
    })().catch(() => {
      preparedAssets.delete(asset.image);
      throw new Error(t('Impossible de charger cette illustration. Réessaie.'));
    });
    preparedAssets.set(asset.image, pending);
  }
  return { ...structuredClone(await pending), name: asset.name };
}

/** No duplicate image or overflow can silently break the symbol-matching rule. */
export function appendCatalogSymbols(game: CardCollection, additions: readonly EditableSymbol[]): EditableSymbol[] {
  const capacity = collectionSymbolCount(game.order);
  const symbols = game.symbols.map(symbol => structuredClone(symbol));
  const seen = new Set(symbols.map(symbol => symbol.image));
  const keys = new Set(symbols.map(symbol => symbol.assetKey).filter(Boolean));
  for (const symbol of additions) {
    if (seen.has(symbol.image) || (symbol.assetKey && keys.has(symbol.assetKey))) continue;
    if (symbols.length === capacity) throw new Error(t('Ce jeu est complet. Retire une image pour en ajouter une autre.'));
    symbols.push({ ...structuredClone(symbol), id: symbols.length });
    seen.add(symbol.image);
    if (symbol.assetKey) keys.add(symbol.assetKey);
  }
  return symbols;
}

export async function createClassicCollection(locale: Locale, onProgress?: (count: number) => void): Promise<CardCollection> {
  const assets = catalogAssets([], locale).filter(asset => asset.origins[0].id === 'classic');
  const symbols: EditableSymbol[] = [];
  for (const asset of assets) {
    symbols.push({ ...await materializeAsset(asset), id: symbols.length });
    onProgress?.(symbols.length);
  }
  const now = Date.now();
  return { id: CLASSIC_GAME_ID, name: locale === 'pt-BR' ? 'Clássico' : 'Classique', order: 7, symbols,
    back: { ...DEFAULT_CARD_BACK }, createdAt: now, updatedAt: now };
}
